import { excludeNullOrUndefined, getBreakdownTitle } from 'checkout/utils'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { isCruiseItem, isTourV1Item } from 'lib/checkout/checkoutUtils'
import createSelector from 'lib/web/createSelector'
import { pluralizeToString } from 'lib/string/pluralize'
import { CABIN_CATEGORIES } from 'constants/cruise'
import moment from 'moment'

import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { getDepositServiceFeeConfig } from 'checkout/selectors/featureConfig/deposit'
import {
  getSelectedCruiseItem,
  getBookingCruiseOffer,
  getBookingRateListData,
  getBookingCabinListData,
  getBookingCabinSelectionData,
  getBookingCabinPricingData,
  getBookingRateDetailsData,
  getBookingCustomShipCategoryGroups,
  getBookingCabinsWithPrice,
  getBookingCabinCategories,
} from 'checkout/selectors/cruiseSelectors'
import { DepositType } from 'constants/checkout'
import { buildCruiseItemView } from 'checkout/lib/utils/cruises/view'
import { buildCruiseSummaryBreakdown } from 'checkout/lib/utils/cruises/summary'
import { getCheckoutRouteFromURL, getIsCabinRateStep } from 'checkout/lib/utils/cruises/steps'
import { getCruiseCountriesByItinerary, getCruisePortsByItinerary, getCruiseDurationByItinerary } from 'lib/cruises/cruiseUtils'
import { sum, EmptyArray, sortBy, groupBy } from 'lib/array/arrayUtils'
import { buildCabinCategoriesGroup, getCruiseDeposits } from 'checkout/lib/utils/cruises/booking'

// Booking V2 - Step Views Selectors
export const getBookingCabinTypeStepView = createSelector(
  getBookingRateListData,
  getBookingCruiseOffer,
  (rateListData, offer): Cruises.BookingCabinTypeStepView => {
    const cabinRates = rateListData.rates
    const cabinCategories: App.CruiseShipCabinCategory[] = offer.ship.cabinCategories ?? EmptyArray
    const offerEvergreenInclusions = offer.evergreenInclusions
    const ratesByType = groupBy(cabinRates, rate => rate.cabinType)

    const cabinTypes = CABIN_CATEGORIES
      .map((cabinType) => {
        const categoryRates = ratesByType.get(cabinType.category) ?? []
        const [lowestRate] = sortBy(categoryRates, rate => rate.priceDetails.price, 'asc')
        const shipCategory = cabinCategories.find(category => category.cabinType === cabinType.category)
        const evergreenInclusions = offerEvergreenInclusions.filter((inclusion) => inclusion.cabinTypes.includes(cabinType.category))

        return {
          name: cabinType.category,
          imageId: shipCategory?.imageId || cabinType.defaultImageId,
          lowestRatePrice: lowestRate?.priceDetails,
          evergreenInclusions,
        }
      })

    return {
      cabinTypes,
      loading: rateListData.loading,
      error: rateListData.error,
      errorMessage: rateListData.errorMessage,
    }
  },
)

export const getBookingCabinCategoryStepView = createSelector(
  getBookingCabinCategories,
  getBookingRateListData,
  getBookingCabinListData,
  getBookingCustomShipCategoryGroups,
  (allCategories, rateListData, cabinListData, customCabinCategoriesGroups): Cruises.BookingCabinCategoryStepView => {
    // If we have custom categories groups coming from Admin Portal, use them
    let customCategoryGroups: Cruises.BookingCabinCategoryGroupView[] = []
    if (customCabinCategoriesGroups.length) {
      customCategoryGroups = customCabinCategoriesGroups.map((group) => {
        const categoryCodes = group.cabinCategories.map(category => category.code)
        const cabinCategories = allCategories.filter(category => categoryCodes.includes(category.code))

        if (!cabinCategories.length) return null
        return buildCabinCategoriesGroup(group.name, cabinCategories)
      }).filter(Boolean) as Cruises.BookingCabinCategoryGroupView[]
    }

    // If we don't have custom categories groups, we use the default ones
    const customCategoryCodes = customCabinCategoriesGroups.map(group => group.cabinCategories.map(category => category.code)).flat()
    const categoriesWithoutCustomGroup = allCategories.filter(category => !customCategoryCodes.includes(category.code))

    const groupedCategories: Map<string, Cruises.BookingCabinCategory[]> = groupBy(
      categoriesWithoutCustomGroup,
      category => category.name.toLowerCase(),
    )
    const defaultCategoryGroups = Array.from(groupedCategories).map(([, cabinCategories]) => {
      const name = cabinCategories[0].name
      return buildCabinCategoriesGroup(name, cabinCategories)
    })

    const categoryGroups = sortBy([
      ...customCategoryGroups,
      ...defaultCategoryGroups,
    ], (group) => group.lowestRatePrice.price, 'asc')

    return {
      categoryGroups,
      error: rateListData.error || cabinListData.error,
      loading: rateListData.loading,
    }
  },
)

export const getBookingCabinLocationStepView = createSelector(
  getBookingCabinsWithPrice,
  getBookingCruiseOffer,
  getBookingCabinCategories,
  getBookingRateListData,
  getBookingCabinListData,
  (cabinsWithPrice, offer, categories, rateListData, cabinListData): Cruises.BookingCabinLocationStepView => {
    const normalCabins = cabinsWithPrice
      .filter(cabin => !cabin.isGuaranteed)

    const cabinsByCategory = Array.from(groupBy(normalCabins, cabin => cabin.lowestCategoryCode))
      .map(([code, cabins]) => {
        const category = categories.find(category => category.code === code)

        if (category) {
          const lowestRatePrice = sortBy(cabins, cabin => cabin.lowestPrice?.price, 'asc')[0]?.lowestPrice
          const { name, color, images, description, evergreenInclusions } = category
          const cabinsCount = cabins.length

          return {
            name,
            code,
            color,
            cabins,
            description,
            images,
            cabinsCount,
            evergreenInclusions,
            lowestRatePrice,
          }
        }

        return null
      }).filter(Boolean) as Cruises.BookingCabinsByCategoryView[]

    const sortedCabinsByCategory = sortBy(cabinsByCategory, category => category.lowestRatePrice?.price, 'asc')
    const lowestCabinPrice = sortBy(normalCabins, cabin => cabin.lowestPrice?.price, 'asc')[0]?.lowestPrice

    const guaranteedCabin = cabinsWithPrice.find(cabin => cabin.isGuaranteed)
    let guaranteedCabinImageId: string = ''

    if (guaranteedCabin) {
      const category = categories.find(category => category.code === guaranteedCabin.lowestCategoryCode)
      guaranteedCabinImageId = category?.images[0]?.id ?? ''
    }

    return {
      isLoading: rateListData.loading || cabinListData.loading,
      shipId: offer.ship.id,
      cabins: normalCabins,
      cabinsByCategory: sortedCabinsByCategory,
      lowestCabinPrice,
      guaranteedCabin,
      guaranteedCabinImageId,
    }
  },
)

export const getBookingCabinPackageStepView = createSelector(
  getBookingRateListData,
  getBookingCabinListData,
  getSelectedCruiseItem,
  (rateListData, cabinListData, cruiseItem): Cruises.BookingCabinPackageStepView => {
    const { rates } = rateListData
    const { cabinByNumber } = cabinListData

    const cabin = cruiseItem.cabinNumber ? cabinByNumber[cruiseItem.cabinNumber] : null
    const cabinComponentIds = cabin?.componentIds ?? []

    const cabinRates = rates.filter((cabinRate) => (
      cruiseItem.cabinCode === cabinRate.pricedCategoryCode &&
      cabinRate.cabinType === cruiseItem.cabinType &&
      cabinComponentIds.includes(cabinRate.componentId)
    ))

    return {
      loading: rateListData.loading || cabinListData.loading,
      error: rateListData.error || cabinListData.error,
      packages: sortBy(cabinRates, (rate) => rate.priceDetails.price, 'asc'),
    }
  },
)

// Other Selectors (move to cruiseSelectors)
export const getCruiseDepositDetailsFromCabinHold = createSelector(
  (state: App.State) => state.cruise.cabinHold,
  getBookingCabinSelectionData,
  (cabinHold, cabinSelectionData): Cruises.CruiseDepositDetails | null => {
    const { cabinSelection } = cabinSelectionData

    if (cabinHold) {
      const deposit = cabinHold.pricing
        .paymentSchedule.find((item) => item.typeId === 1)

      if (!deposit) return null

      return {
        dueDate: deposit.date,
        amount: deposit.amount,
        currencyCode: cabinHold.pricing.currency,
      }
    }

    if (cabinSelection) {
      const deposit = cabinSelection.pricing.paymentSchedule?.firstPayment

      if (!deposit) return null

      return {
        amount: deposit.amount,
        dueDate: deposit.dueDate,
        currencyCode: deposit.currencyCode,
      }
    }

    return null
  },
)

export const getCruiseOnboardCreditDetails = createSelector(
  (state: App.State) => state.cruise.cabinHold,
  getBookingCabinSelectionData,
  getBookingCabinPricingData,
  (cabinHold, cabinSelectionData, cabinPricingData): Cruises.CruiseOnboardCreditDetails | null => {
    const { cabinSelection } = cabinSelectionData

    if (cabinPricingData.hasCabinPricing) {
      const onboardCreditsV1 = cabinPricingData.cabinPricingV1?.pricing.onboardCredits
      const onboardCreditsV2 = cabinPricingData.cabinPricing?.pricing.onboardCredit

      if (onboardCreditsV1) {
        return {
          amount: onboardCreditsV1.amount,
          currencyCode: onboardCreditsV1.currencyCode,
        }
      }

      if (onboardCreditsV2?.amount && onboardCreditsV2?.currency) {
        return {
          amount: onboardCreditsV2.amount,
          currencyCode: onboardCreditsV2.currency,
        }
      }

      return null
    }

    if (cabinHold) {
      const onboardCredits = cabinHold.pricing.onboardCredits

      if (!onboardCredits) return null

      return {
        amount: onboardCredits.amount,
        currencyCode: onboardCredits.currencyCode,
      }
    }

    if (cabinSelection) {
      const onboardCredits = cabinSelection.pricing.onboardCredit

      if (!onboardCredits?.amount || !onboardCredits?.currency) return null

      return {
        amount: onboardCredits.amount,
        currencyCode: onboardCredits.currency,
      }
    }

    return null
  },
)

// Legacy Selectors
export const getCruiseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.CruiseItem> => items.filter(isCruiseItem),
)

/** SOME RATES HAVE A WAITING LIST CATEGORY AND
 * WE ONLY KNOW WHEN "/CABIN-AVAILABILITY" IS REQUESTED

 * WE NEED TO SAVE THOSE CATEGORIES IN THE STATE AND FILTER
 * THE DATA SO AS TO NOT DISPLAY THE SAME UNAVAILABLE RATE **/
export const getCabinRates = createSelector(
  (state: App.State) => state.cruise.cabinRates,
  (state: App.State) => state.cruise.waitListedCategories,
  (cabinRates, waitListedCategories): Array<App.CruiseCabinRateView> => {
    return cabinRates?.filter((item) =>
      !waitListedCategories.includes(item.componentId)).filter(Boolean) ?? EmptyArray
  },
)

export function getCruiseBreakdownView(
  offerView: App.Checkout.CruiseAccommodationOfferView,
  hidePrice: boolean,
): App.Checkout.PriceBreakdownView {
  const { startDate, endDate, saleUnit, itemViews, mainLabel } = offerView

  const cruiseDuration = getCruiseDurationByItinerary(offerView.offer?.itinerary ?? [])

  const items = itemViews.map<App.Checkout.ItemBreakdownView>(
    (itemView: App.Checkout.CruiseAccommodationItemView, index) => {
      const { additionalElements, additionalInfoText } = buildCruiseSummaryBreakdown(itemView)

      const onboardCreditsV1 = itemView.cabinRateView?.onboardCredits || itemView.cabinHold?.pricing?.onboardCredits
      const onboardCreditsV2 = itemView.cabinSelection?.pricing?.onboardCredit

      const onboardCreditAmount = onboardCreditsV1?.amount || onboardCreditsV2?.amount
      const onboardCreditCurrencyCode = onboardCreditsV1?.currencyCode || onboardCreditsV2?.currency

      const hasOnboardCredits = !!(onboardCreditAmount && onboardCreditCurrencyCode)

      const cancellationPolicyDetails =
        itemView.cabinHold?.pricing.cancellationPolicy ??
        itemView.cabinSelection?.pricing.cancellationPolicy

      const price = (hidePrice || !window.location.pathname.includes('purchase')) ? undefined : (itemView?.price ?? 0) - itemView.taxesAndFees
      const title = [`${saleUnit} ${index + 1}`, itemView.item?.cabinType].filter(Boolean).join(' - ')

      return {
        title,
        additionalInfoText,
        cancellationPolicy: cancellationPolicyDetails,
        additionalElements,
        price,
        taxesAndFees: itemView.taxesAndFees,
        offerId: itemView.item.offerId,
        itemType: 'cruise',
        ...(hasOnboardCredits && {
          onboardCredits: {
            amount: onboardCreditAmount,
            currencyCode: onboardCreditCurrencyCode,
          },
        }),
      }
    })

  const formattedStartDate = startDate ? `${moment(startDate).format(DMY_CASUAL_FORMAT)}` : 'start'
  const formattedEndDate = endDate ? `${moment(endDate).format(DMY_CASUAL_FORMAT)}` : 'end'
  const price = sum(items, item => (item.price ?? 0))
  const memberPrice = sum(items, item => (item.memberPrice ?? 0))

  return {
    title: getBreakdownTitle(offerView),
    description: mainLabel,
    additionalInfoText: [
      `${offerView.offer?.cruiseLine.name} - ${offerView.offer?.ship.name}`,
      `${formattedStartDate} - ${formattedEndDate} - ${pluralizeToString('night', cruiseDuration - 1)}`,
    ],
    items,
    price,
    memberPrice,
    onboardCredits: items.find(item => item.onboardCredits)?.onboardCredits,
    productType: offerView.offerType,
  }
}

export const getCheckoutCruiseOfferView = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.cruiseOffers,
  (state: App.State) => state.cruise.cabinRates,
  (state: App.State) => state.cruise.cabinRateDetails,
  (state: App.State) => state.cruise.cruiseDeparture,
  (state: App.State) => state.cruise.cabinAvailability,
  (state: App.State) => state.cruise.mapLegend,
  (state: App.State) => state.cruise.deckSelections,
  (state: App.State) => state.cruise.cabinHold,
  getDepositServiceFeeConfig,
  getBookingCabinPricingData,
  getBookingCabinSelectionData,
  getBookingRateListData,
  getBookingRateDetailsData,
  (state: App.State) => state.router.location.pathname,
  (
    items,
    offers,
    rates,
    rateDetails,
    departures,
    availableCabinList,
    legendList,
    deckSelections,
    cabinHold,
    serviceFee,
    cabinPricingData,
    cabinSelectionData,
    rateListData,
    rateDetailsData,
    pathname,
  ): App.WithDataStatus<Array<App.Checkout.CruiseAccommodationOfferView>> => {
    if (items.length === 0) { return { hasRequiredData: true, data: [] } }

    const { cabinSelection } = cabinSelectionData
    const item = items[0]
    const offer = offers[item.offerId]

    const dayDuration = getCruiseDurationByItinerary(offer?.itinerary)
    const countriesCount = getCruiseCountriesByItinerary(offer?.itinerary).length
    const portsCount = getCruisePortsByItinerary(offer?.itinerary).length

    const cruiseCheckoutStep = getCheckoutRouteFromURL(pathname)
    const isCabinRateStep = getIsCabinRateStep(cruiseCheckoutStep)

    let hasRequiredData = false
    const offerView: App.Checkout.CruiseAccommodationOfferView = {
      offer,
      offerId: item.offerId,
      mainLabel: offer?.name,
      duration: offer?.duration,
      startDate: departures?.[item.departureId]?.departureDate,
      endDate: departures?.[item.departureId]?.returnDate,
      image: offer?.images?.[0],
      location: getLocationString(offer),
      occupancy: items.map(item => item.occupancy).filter(excludeNullOrUndefined),
      offerType: offer?.type,
      reservationType: 'instant_booking',
      saleUnit: 'cabin',
      durationLabel: pluralizeToString('night', dayDuration - 1),
      offerLoaded: !!offer,
      itemViews: [],
      propertyTimezone: 'GMT',
      operatorName: offer ? `Operated by: ${offer.cruiseLine.name}` : '',
      shipName: offer?.ship ? `Cruise ship: ${offer.ship.name}` : '',
      countries: countriesCount,
      ports: portsCount,
      serviceFee,
      depositType: DepositType.FLAT,
      designation: 'Cruise',
      ship: offer?.ship,
      availableCabinList: availableCabinList ?? [],
      legendList,
      deckSelections: deckSelections ?? {},
      urgencyLabels: [],
      bedGroups: [],
      confidenceLabels: [],
    }

    if (item.version === 'v2') {
      const depositData = getCruiseDeposits(cabinPricingData)
      const depositAmount = depositData?.schedule?.firstPayment?.amount || null
      const cabinRate = rateListData.rates.find(rate => rate.componentId === item.componentId)

      const details = rateDetailsData.detailsByRate[item.componentId || ''] as App.CruiseCabinRateDetailsResponse
      const itemViewWithStatus = buildCruiseItemView(item, offer, undefined, undefined, details, cabinPricingData, cabinSelection, cabinRate)

      // The data required is different according to the checkout stage
      hasRequiredData = isCabinRateStep ? !!offer : [
        itemViewWithStatus.hasRequiredData,
        !!offer,
        !!cabinSelection,
      ].every(Boolean)

      return {
        hasRequiredData,
        data: [{
          ...offerView,
          itemViews: [itemViewWithStatus.data],
          ...(depositAmount && { depositAmount }),
        }],
      }
    }

    const rate = rates ? rates.find(rate => rate.componentId === item.componentId) : undefined
    const depositData = getCruiseDeposits(cabinPricingData)
    const depositAmount = depositData?.schedule?.firstPayment?.amount || null

    const details = rateDetails?.[item.componentId || '']
    const itemViewWithStatus = buildCruiseItemView(item, offer, rate, cabinHold, details, cabinPricingData)

    // The data required is different according to the checkout stage
    hasRequiredData = isCabinRateStep ? !!offer : [
      itemViewWithStatus.hasRequiredData,
      !!availableCabinList && availableCabinList.length > 0,
      !!offer,
      !!rate,
    ].every(Boolean)

    return {
      hasRequiredData,
      data: [{
        ...offerView,
        itemViews: [itemViewWithStatus.data],
        ...(depositAmount && { depositAmount }),
      }],
    }
  },
)

export const getCheckoutCruiseOffer = createSelector(
  getCheckoutCruiseOfferView,
  (offerViewWithStatus): App.CruiseOffer | undefined => offerViewWithStatus.data[0]?.offer,
)

export const getCruisesV1 = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (items, leOffers) => {
    return items.filter(isTourV1Item).filter(item => {
      const offer = leOffers[item.offerId]
      return !!offer && !!offer.holidayTypes?.includes('Cruises')
    })
  },
)

export const isCruiseCheckout = createSelector(
  getCruiseItems,
  getCruisesV1,
  (state: App.State) => state.checkout.cart.existingOrder?.cruiseItems,
  (cruiseItems, cruiseOfTourV1Items, existingCruiseItems) => {
    return !!cruiseItems.length || !!cruiseOfTourV1Items.length || !!existingCruiseItems?.length
  },
)
